import React, { Component } from 'react';
import _ from 'lodash';
import { getAccountId, getUserName } from '../../localStorage';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './styles.scss';
import {
  Form,
  FormGroup,
  Label,
  Col,
  Input,
  Row,
  FormFeedback,
} from 'reactstrap';
import { SLIDE_UP } from 'react-ladda';
import Select from 'react-select';
import { countryList, stateUSData } from '../../constantsApp/countryList';
import { getCities, getZips } from '../../utils/utils';
import {
  fieldKeyOf,
  getCcpList,
  getRegionFields,
  getRegionResets,
  getTenantLabel,
} from '../../utils/tenantFieldConfig';
import {
  ADDRESS_FIELDS,
  CONTACT_FIELDS,
  selfCareAddressPayload,
  selfCareContactPayload,
  selfCareFields,
} from '../../utils/selfCareFieldConfig';
import { ButtonCustom } from '../common';

/* The account's PRIMARY record (contact or address), else its first one, else {} */
export const primaryRecord = records => {
  const list = records || [];
  return list.find(record => record && record.id === 'PRIMARY') || list[0] || {};
};

class ManageProfileForm extends Component {
  constructor() {
    super();
    this.state = {
      profile: {},
      firstName: '',
      middleName: '',
      lastName: '',
      identityDocument: '',
      email: '',
      phoneNumber: '',
      isSubmitting: false,
      hadPressSubmit: false,
      street: '',
      city: '',
      state: '',
      country: 'USA',
      postalCode: '',
      district: '',
      regions: null,
      userId: getUserName(),
      password: '',
      retypePassword: '',
      optionsCities: [],
      isChecked: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { profile } = nextProps;
    if (
      JSON.stringify(this.state.profile) !== JSON.stringify(nextProps.profile)
    ) {
      // A profile save writes the PRIMARY contact and address (engine PGSelfCareService), so show those,
      // whatever order the account's records come in; else the first one, as before
      const contact = primaryRecord(profile.contacts);
      const address = primaryRecord(profile.addresses);

      this.setState({
        profile,
        firstName: contact.firstName,
        middleName: contact.middleName,
        lastName: contact.lastName,
        identityDocument: contact.identityDocument,
        email: contact.email,
        phoneNumber:
          contact.phones && contact.phones.length > 0 && contact.phones[0]
            ? contact.phones[0].number
            : '',
        street: address.street,
        country: address.country,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        district: address.district,
        password: '',
      });
    }
  }

  componentDidMount() {
    this.loadRegions();
  }

  componentDidUpdate() {
    this.loadRegions();
  }

  // The tenant's region lists (Provincia/Cantón/Distrito...) for the current country, once per country.
  loadRegions = () => {
    const { ccpPropertyList, getAddressRegions } = this.props;
    const { country, regions, profile } = this.state;
    const fields = getRegionFields(ccpPropertyList);
    // Wait for the profile, or the constructor's placeholder country is loaded first.
    if (_.isEmpty(profile) || !country || !fields.length || !getAddressRegions) return;
    if (regions && regions.country === country) return;
    this.setState({ regions: { country } });
    fields.forEach(field => {
      getAddressRegions({ country, field }, ({ data }) => {
        this.setState(({ regions: current }) =>
          current && current.country === country ? { regions: { ...current, [field]: data || [] } } : null,
        );
      });
    });
  };

  // Form entries for one prefix (contact/address) with the tenant's ccp config applied.
  fieldsFor = (dataField, prefix) => {
    const { ccpPropertyList, i18n } = this.props;
    const { regions, country } = this.state;
    return selfCareFields({
      dataField,
      ccpPropertyList,
      prefix,
      language: i18n && i18n.language,
      regions: regions && regions.country === country ? regions : null,
      data: this.state,
    });
  };

  hasRegions = () => getRegionFields(this.props.ccpPropertyList).length > 0;

  // A region level or country change clears the levels below it; postal code is never touched.
  // Re-picking the value already held clears nothing.
  onChangeSelect = (name, value) => {
    this.setState({ [name]: value, ...getRegionResets(this.props.ccpPropertyList, name, value, this.state) });
  };

  onChangeText = evt => {
    this.setState({ [evt.target.name]: evt.target.value });
  };

  onChangeCheckBox = ({ name, value }) => {
    this.setState({ [name]: value });
  };

  onBlurUserId = () => {};

  onChangeCountry = countryVal => {
    if (this.hasRegions()) return this.onChangeSelect('country', countryVal);
    const { country } = this.state;
    if (
      (country && country !== countryVal) ||
      (!country && countryVal === 'USA')
    ) {
      this.setState({
        state: '',
        city: '',
        postalCode: '',
        country: countryVal,
      });
    } else {
      this.setState({ country: countryVal });
    }
  };

  onChangeState = stateVal => {
    if (this.hasRegions()) return this.onChangeSelect('state', stateVal);
    const { state } = this.state;
    if (state && state !== stateVal) {
      this.setState({
        state: stateVal,
        city: '',
        postalCode: '',
      });
    } else {
      this.setState({ state: stateVal });
    }
  };

  onChangeCity = cityVal => {
    if (this.hasRegions()) return this.onChangeSelect('city', cityVal);
    const { city } = this.state;
    if (city && city !== cityVal) {
      this.setState({
        city: cityVal,
        postalCode: '',
      });
    } else {
      this.setState({ city: cityVal });
    }
  };

  onChangePostalCode = postalCodeVal => {
    this.setState({ postalCode: postalCodeVal });
  };

  handleSubmit = evt => {
    evt.preventDefault();

    const {
      firstName,
      middleName,
      lastName,
      identityDocument,
      district,
      email,
      phoneNumber,
      street,
      state,
      city,
      postalCode,
      userId,
      password,
      retypePassword,
      profile,
      country,
    } = this.state;

    if (password !== retypePassword) return alert('Please fill valid form');
    const { manageAccount, ccpPropertyList } = this.props;
    // Fields this tenant does not show are not sent, so other tenants' payloads do not change.
    const shown = [
      ...this.fieldsFor(CONTACT_FIELDS, 'contact'),
      ...this.fieldsFor(ADDRESS_FIELDS, 'address'),
    ].map(fieldKeyOf);
    const ifShown = (key, value) => (shown.indexOf(key) > -1 ? { [key]: value } : {});
    const paymentProfile =
      profile &&
      profile.paymentProfiles &&
      profile.paymentProfiles.length > 0 &&
      profile.paymentProfiles[0]
        ? profile.paymentProfiles[0]
        : {};
    const creditCard =
      paymentProfile &&
      paymentProfile.creditCards &&
      paymentProfile.creditCards.length > 0 &&
      paymentProfile.creditCards[0]
        ? paymentProfile.creditCards[0]
        : {};
    return manageAccount(
      {
        accountId: getAccountId(),
        userId,
        password,
        selfCareAddress: selfCareAddressPayload(
          { street, city, state, country, postalCode, ...ifShown('district', district) },
          ccpPropertyList,
        ),
        selfCareContact: selfCareContactPayload(
          {
            firstName,
            ...ifShown('middleName', middleName),
            lastName,
            ...ifShown('identityDocument', identityDocument),
            email,
            number: phoneNumber,
          },
          ccpPropertyList,
        ),
      },
      () => {
        this.setState({ isSubmitting: false });
      },
    );
  };

  // tenantLabel: the tenant's address.stateLabel, or '' for the platform wording.
  renderState(tenantLabel) {
    const { country, state, hadPressSubmit } = this.state;
    if (country === 'USA') {
      let stateSelected = null;
      if (country === 'USA' && state) {
        const st = stateUSData.find(el => el.value === state);
        stateSelected = st
          ? { label: `${st.label} (${st.value})`, value: st.value }
          : null;
      }
      return (
        <FormGroup>
          <Label for="country">{`${tenantLabel || 'State'}:`}</Label>
          <Select
            className={state ? '' : 'select-field-error'}
            value={stateSelected}
            options={stateUSData.map(item => ({
              label: `${item.label} (${item.value})`,
              value: item.value,
            }))}
            onChange={val => this.onChangeState(val.value)}
          />
        </FormGroup>
      );
    }

    return (
      <FormGroup>
        <Label for="state">{`${tenantLabel || 'State'}:`}</Label>
        <Input
          type="text"
          name="state"
          id="state"
          placeholder={tenantLabel || 'State'}
          value={state}
          onChange={this.onChangeText}
        />
        <FormFeedback valid={!hadPressSubmit}>
          <b>{tenantLabel || 'State'}</b>
          {' '}
can not be blank.
        </FormFeedback>
      </FormGroup>
    );
  }

  getCitiesFormState = () => {
    const { state } = this.state;
    const options =
      getCities(state).map(item => ({
        label: item.city,
        value: item.city,
      })) || [];
    this.setState({ optionsCities: options });
  };

  onSetDefaulOptionCities = () => {
    this.setState({ optionsCities: [] });
  };

  renderCity(tenantLabel) {
    const { city, hadPressSubmit, country, optionsCities } = this.state;
    const { t } = this.props;
    const label = tenantLabel || t('label.city');
    if (country === 'USA') {
      let citySelected = null;
      if (city) {
        citySelected = { value: city, label: city };
      }
      return (
        <FormGroup>
          <Label for="city">{`${label}:`}</Label>
          <Select
            className={city ? '' : 'select-field-error'}
            value={citySelected}
            onChange={val => this.onChangeCity(val.value)}
            id="city"
            options={optionsCities}
            onFocus={this.getCitiesFormState}
            onBlur={this.onSetDefaulOptionCities}
          />
        </FormGroup>
      );
    }
    return (
      <FormGroup>
        <Label for="city">{`${label}:`}</Label>
        <Input
          type="text"
          name="city"
          id="city"
          placeholder={tenantLabel || 'City'}
          value={city}
          onChange={this.onChangeText}
        />
        <FormFeedback valid={!hadPressSubmit}>
          <b>{label}</b> 
          {' '}
          {`${t('message.canNotBeBlank')}`}
        </FormFeedback>
      </FormGroup>
    );
  }

  renderPostalCode(tenantLabel) {
    const { t } = this.props;
    const label = tenantLabel || t('label.postalCode');
    const { postalCode, hadPressSubmit, country, state, city } = this.state;
    if (country === 'USA') {
      let postalCodeSelected = null;
      if (postalCode) {
        postalCodeSelected = { value: postalCode, label: postalCode };
      }
      return (
        <FormGroup>
          <Label for="country">{`${label}:`}</Label>
          <Select
            name="address_postalCode"
            className={postalCode ? '' : 'select-field-error'}
            options={
              state && city
                ? getZips(city, state).map(item => ({
                    label: item.zipcode,
                    value: item.zipcode,
                  }))
                : [
                    {
                      label: 'Please select City before',
                      value: null,
                      isDisabled: true,
                    },
                  ]
            }
            value={postalCodeSelected}
            onChange={val => this.onChangePostalCode(val.value)}
          />
        </FormGroup>
      );
    }
    return (
      <FormGroup>
        <Label for="postalCode">{`${label}:`}</Label>
        <Input
          type="text"
          name="postalCode"
          id="postalCode"
          placeholder={tenantLabel || 'postalCode'}
          value={postalCode}
          onChange={this.onChangeText}
        />
        <FormFeedback valid={!hadPressSubmit}>
          <b>{label}</b> 
          {' '}
          {`${t('message.canNotBeBlank')}`}
        </FormFeedback>
      </FormGroup>
    );
  }

  renderCountry(tenantLabel) {
    const { t } = this.props;
    const { country, hadPressSubmit } = this.state;
    let countrySelected = null;
    if (country) {
      const ctr = countryList.find(el => el.value === country);
      countrySelected = {
        label: ctr ? `${ctr.label} (${ctr.value})` : null,
        value: ctr ? ctr.value : null,
      };
    }
    // Not clearable: the Country scalar rejects "", so País can be changed but not emptied.
    return (
      <FormGroup>
        <Label for="country">{`${tenantLabel || t('label.country')}:`}</Label>
        <Select
          value={countrySelected}
          options={countryList.map(item => ({
            label: `${item.label} (${item.value})`,
            value: item.value,
          }))}
          placeholder={tenantLabel || 'Country'}
          onChange={val => this.onChangeCountry(val.value)}
          className="form__form-group-select"
          inputProps={{
            autoComplete: 'off',
            autoCorrect: 'off',
            spellCheck: 'off',
          }}
        />
        <FormFeedback valid={!hadPressSubmit}>
          <b>{tenantLabel || t('label.Country')}</b>
          {`${t('message.canNotBeBlank')}`}
        </FormFeedback>
      </FormGroup>
    );
  }

  // A tenant dropdown (region level, Tipo de documento); a stored value outside the list stays as an option.
  renderSelect(item, prefix, label, disabled) {
    const key = fieldKeyOf(item);
    const value = this.state[key];
    const optional = getCcpList(this.props.ccpPropertyList, `${prefix}OptionalFields`);
    return (
      <FormGroup>
        <Label for={key}>{`${label}:`}</Label>
        <Select
          id={key}
          value={item.optionSelect.find(option => option.value === value) || null}
          options={item.optionSelect}
          placeholder={label}
          isClearable={key !== 'country' && optional.indexOf(key) > -1}
          isDisabled={disabled}
          onChange={val => this.onChangeSelect(key, val ? val.value : '')}
          className="form__form-group-select"
        />
      </FormGroup>
    );
  }

  // Without a scoped <prefix>.<key>Label row each field keeps its platform wording and placeholder.
  renderField(item, prefix, disabled) {
    const { t, ccpPropertyList, i18n } = this.props;
    const { hadPressSubmit } = this.state;
    const key = fieldKeyOf(item);
    const tenantLabel = getTenantLabel(ccpPropertyList, `${prefix}.${key}`, i18n && i18n.language);
    const label = tenantLabel || t(item.labelFilter);
    if (key === 'country') return this.renderCountry(tenantLabel);
    if (item.optionSelect) return this.renderSelect(item, prefix, label, disabled);
    if (key === 'state') return this.renderState(tenantLabel);
    if (key === 'city') return this.renderCity(tenantLabel);
    if (key === 'postalCode') return this.renderPostalCode(tenantLabel);
    return (
      <FormGroup>
        <Label for={key}>{`${label}:`}</Label>
        <Input
          type="text"
          name={key}
          id={key}
          disabled={disabled}
          placeholder={item.placeholder || (key === 'street' ? tenantLabel || 'Street' : undefined)}
          value={this.state[key]}
          onChange={this.onChangeText}
        />
        <FormFeedback valid={!hadPressSubmit}>
          <b>{label}</b>
          {`${t('message.canNotBeBlank')}`}
        </FormFeedback>
      </FormGroup>
    );
  }

  renderBasicInfo() {
    const {
      email,
      phoneNumber,
      userId,
      password,
      retypePassword,
      hadPressSubmit,
      isChecked,
    } = this.state;

    const { modeModifyContact, t } = this.props;

    return (
      <div>
        <Col md={12}>
          <Row>
            {modeModifyContact !== 0 &&
              this.fieldsFor(CONTACT_FIELDS, 'contact').map(item => (
                <Col md={4} key={fieldKeyOf(item)}>
                  {this.renderField(item, 'contact', modeModifyContact === 1)}
                </Col>
              ))}
            {this.fieldsFor(ADDRESS_FIELDS, 'address').map(item => (
              <Col md={4} key={fieldKeyOf(item)}>
                {this.renderField(item, 'address', false)}
              </Col>
            ))}
            {modeModifyContact !== 0 && (
              <Col sm={4}>
                <FormGroup>
                  <Label for="email">{`${t('label.email')}:`}</Label>
                  <Input
                    type="email"
                    name="email"
                    id="email"
                    disabled={modeModifyContact === 1}
                    value={email}
                    onChange={this.onChangeText}
                  />
                  <FormFeedback valid={!hadPressSubmit}>
                    <b>{`${t('label.email')}`}</b>
                    {`${t('message.canNotBeBlank')}`}
                  </FormFeedback>
                </FormGroup>
              </Col>
            )}
            {modeModifyContact !== 0 && (
              <Col sm={4}>
                <FormGroup>
                  <Label for="phoneNumber">
                    {`${t(
                    'label.phoneNumber',
                  )}:`}

                  </Label>
                  <Input
                    type="text"
                    name="phoneNumber"
                    id="phoneNumber"
                    disabled={modeModifyContact === 1}
                    value={phoneNumber}
                    onChange={this.onChangeText}
                  />
                  <FormFeedback valid={!hadPressSubmit}>
                    <b>{`${t('label.phoneNumber')}`}</b>
                    {`${t('message.canNotBeBlank')}`}
                  </FormFeedback>
                </FormGroup>
              </Col>
            )}
            <Col md={4}>
              <FormGroup>
                <Label for="userId">{`${t('label.userId')}:`}</Label>
                <Input
                  type="text"
                  name="userId"
                  id="userId"
                  placeholder="User Id"
                  value={userId}
                  onChange={this.onChangeText}
                  onBlur={this.onBlurUserId}
                />
                <FormFeedback valid={!hadPressSubmit}>
                  <b>{`${t('label.userId')}`}</b>
                  {`${t('message.canNotBeBlank')}`}
                </FormFeedback>
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label for="password">{`${t('label.password')}:`}</Label>
                <Input
                  type="password"
                  name="password"
                  id="password"
                  placeholder="***"
                  value={password}
                  onChange={this.onChangeText}
                />
                <FormFeedback valid={!hadPressSubmit}>
                  <b>{`${t('label.password')}`}</b>
                  {`${t('message.canNotBeBlank')}`}
                </FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="retypePassword">
                  {`${t(
                  'label.retypePassword',
                )}:`}

                </Label>
                <Input
                  type="password"
                  invalid={password !== retypePassword}
                  name="retypePassword"
                  id="re-password"
                  placeholder="***"
                  value={retypePassword}
                  onChange={this.onChangeText}
                />
                <FormFeedback valid={!hadPressSubmit}>
                  <b>{`${t('label.retypePassword')}`}</b>
                  {`${t('message.canNotBeBlank')}`}
                </FormFeedback>
              </FormGroup>
            </Col>
            <Col md={4} />
            <Col md={4} />
            <Col md={4}>
              <FormGroup style={{ paddingLeft: 30 }}>
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="customCheck1"
                  checked={isChecked}
                  onChange={evt =>
                    this.onChangeCheckBox({
                      name: 'isChecked',
                      value: evt.target.checked,
                    })
                  }
                />
                <label className="custom-control-label" htmlFor="customCheck1">
                  {`${t('label.rememberMe')}`}
                </label>
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </div>
    );
  }

  render() {
    const { isSubmitting } = this.state;
    const { modeModifyUser, t } = this.props;
    return (
      <Form
        onSubmit={this.handleSubmit}
        className="title-form title-form-detail"
      >
        {this.renderBasicInfo()}
        <Row className="mt-sm-0 mx-0 row">
          <Col sm={{ size: 12, offset: 8 }} className="row float-right">
            <ButtonCustom
              loading={isSubmitting}
              className="ml-auto mt-auto mb-auto mr-2 btn-cancel"
              type="button"
              title={t('label.cancel')}
              titleloading="Modifying"
              datastyle={SLIDE_UP}
              onClick={() => {
                this.props.history.push('/');
              }}
            />
            {modeModifyUser !== 0 && (
              <ButtonCustom
                className="btn-submit"
                type="submit"
                title={t('label.submit')}
                disabled={modeModifyUser === 1}
                datastyle={SLIDE_UP}
              />
            )}
          </Col>
        </Row>
      </Form>
    );
  }
}

ManageProfileForm.propTypes = {
  manageAccount: PropTypes.func,
  ccpPropertyList: PropTypes.arrayOf(PropTypes.object),
  getAddressRegions: PropTypes.func,
  profile: PropTypes.object,
  modeModifyUser: PropTypes.number,
  modeModifyContact: PropTypes.number,
};

export default withTranslation('common')(ManageProfileForm);
